import React, { useEffect, useRef, useState } from 'react';
import { usePracticeContent } from '../hooks/usePracticeContent';

const NAVY = '#002F6C';
const INK = '#3A3530';

const CORMORANT = '"Cormorant Garamond", serif';
const INTER = 'Inter, sans-serif';
const LATO = 'Lato, sans-serif';

const PracticeDisciplines = () => {
  const { data } = usePracticeContent();
  const band = (data && data.images && data.images.landscape_band) || { url: '', caption: '' };

  const reduced =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const [pct, setPct] = useState(reduced ? 92 : 0);
  const [filled, setFilled] = useState(!!reduced);
  const rowRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (reduced) return undefined;
    const el = rowRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting && !startedRef.current) {
          startedRef.current = true;
          setFilled(true);
          const dur = 1100;
          const t0 = performance.now();
          const step = (t) => {
            const p = Math.min(1, (t - t0) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            setPct(Math.round(eased * 92));
            if (p < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
          io.disconnect();
        }
      });
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, [reduced]);

  return (
    <section data-testid="practice-disciplines-section" style={{ padding: '160px 0', background: '#ffffff' }}>
      <style>{`
        .disc-eyebrow{ font-family:${INTER}; font-size:11.5px; letter-spacing:.3em; color:#185FA5; text-transform:lowercase; margin-bottom:8px; }
        .disc-row{ position:relative; overflow:hidden; padding:46px 0 42px; }
        .disc-row.bordered{ border-top:1px solid rgba(58,53,48,.12); }
        .disc-name-l{ font-family:${CORMORANT}; font-style:italic; font-weight:500; color:${NAVY}; line-height:1.05; font-size:clamp(42px,7.4vw,96px); }
        .disc-name-i{ font-family:${CORMORANT}; font-style:italic; font-weight:500; color:${NAVY}; line-height:1.05; font-size:clamp(33px,5.6vw,72px); opacity:.95; }
        .disc-name-a{ font-family:${CORMORANT}; font-style:italic; font-weight:500; color:${NAVY}; line-height:1.05; font-size:clamp(27px,4.5vw,58px); opacity:.9; }
        .disc-sub{ font-family:${CORMORANT}; font-style:italic; font-weight:500; color:${NAVY}; opacity:.55; font-size:clamp(20px,2.8vw,34px); margin-left:.35em; }
        .disc-share{ display:flex; align-items:center; gap:26px; max-width:820px; margin:30px 0 26px; }
        .disc-track{ flex:1; height:3px; background:rgba(0,47,108,.12); border-radius:3px; overflow:hidden; }
        .disc-fill{ height:100%; background:${NAVY}; border-radius:3px; width:0; transition:width 1.1s ease; }
        .disc-figure{ font-family:${INTER}; font-weight:200; font-size:clamp(44px,6vw,84px); color:${NAVY}; letter-spacing:-.03em; line-height:1; font-variant-numeric:tabular-nums; }
        .disc-figure-label{ display:block; font-family:${INTER}; font-size:10px; font-weight:500; letter-spacing:.24em; color:${INK}; text-transform:lowercase; margin-top:8px; }
        .disc-desc{ font-family:${LATO}; font-weight:300; font-size:16px; line-height:1.65; max-width:600px; color:${INK}; text-transform:lowercase; }
        .disc-band-wrap{ margin:0 0; }
        .disc-band{ position:relative; width:100%; height:clamp(300px,42vw,560px); overflow:hidden; }
        .disc-band-img{ position:absolute; top:0; left:0; height:100%; width:120%; max-width:none; object-fit:cover; object-position:center;
          animation:discPan 16s ease-in-out infinite alternate; will-change:transform; }
        .disc-band-ph{ position:absolute; inset:0; background:linear-gradient(120deg,#6E7D4A 0%,#8B7B65 55%,#C9BFB0 100%); }
        .disc-band-cap{ position:absolute; left:44px; bottom:22px; font-family:${INTER}; font-size:11px; letter-spacing:.16em; color:#ffffff; text-transform:lowercase; text-shadow:0 1px 6px rgba(0,0,0,.4); }
        @keyframes discPan{ from{ transform:translateX(0); } to{ transform:translateX(-14%); } }
        @media (prefers-reduced-motion: reduce){ .disc-band-img{ animation:none; will-change:auto; } .disc-fill{ transition:none; } }
      `}</style>

      <div className="pr-spine">
        <p className="disc-eyebrow" data-testid="practice-disciplines-eyebrow">disciplines</p>

        {/* Row 1 — landscape architecture */}
        <div ref={rowRef} className="disc-row" data-testid="practice-discipline-landscape">
          <div>
            <span className="disc-name-l">landscape architecture</span>
            <span className="disc-sub">&amp; masterplanning</span>
          </div>
          <div className="disc-share">
            <div className="disc-track">
              <div className="disc-fill" style={{ width: filled ? '92%' : '0%' }} data-testid="practice-landscape-bar" />
            </div>
            <div>
              <span className="disc-figure" data-testid="practice-landscape-figure">{pct}%</span>
              <span className="disc-figure-label">of our projects are landscape-led</span>
            </div>
          </div>
          <p className="disc-desc">
            the core of the studio. gardens, farmhouses, public grounds and campuses — and the estate masterplans they grow into — designed for this climate and the plants that belong to it.
          </p>
          <span className="pr-ghost" aria-hidden="true" data-testid="practice-ghost-01">01</span>
        </div>
      </div>

      {/* Full-bleed landscape band */}
      <div className="disc-band-wrap" data-testid="practice-landscape-band">
        <div className="disc-band">
          {band.url ? (
            <img className="disc-band-img" src={band.url} alt={band.caption || 'landscape'} />
          ) : (
            <div className="disc-band-ph" />
          )}
          <span className="disc-band-cap" data-testid="practice-landscape-band-caption">
            {band.url ? (band.caption || '') : 'landscape photograph'}
          </span>
        </div>
      </div>

      <div className="pr-spine">
        {/* Row 2 — interiors */}
        <div className="disc-row bordered" data-testid="practice-discipline-interiors">
          <div><span className="disc-name-i">interiors</span></div>
          <p className="disc-desc" style={{ marginTop: 22 }}>
            rooms that carry the outside in. material, light and green continue past the threshold.
          </p>
          <span className="pr-ghost" aria-hidden="true" data-testid="practice-ghost-02">02</span>
        </div>

        {/* Row 3 — architecture */}
        <div className="disc-row bordered" data-testid="practice-discipline-architecture">
          <div><span className="disc-name-a">architecture</span></div>
          <p className="disc-desc" style={{ marginTop: 22 }}>
            buildings that take their cue from the land they sit on.
          </p>
          <span className="pr-ghost" aria-hidden="true" data-testid="practice-ghost-03">03</span>
        </div>
      </div>
    </section>
  );
};

export default PracticeDisciplines;
